import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export interface ChatConversation {
  projectId: string;
  customerId: string;
  customerName: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  // Extra project metadata for hover card
  projectTitle?: string;
  projectAddress?: string;
  projectStatus?: string;
  projectCreatedAt?: string;
  projectStartDate?: string;
}

interface ChatContextType {
  conversations: ChatConversation[];
  activeConversationId: string | null;
  isOpen: boolean;
  isMinimized: boolean;
  registerChat: (conv: ChatConversation) => void;
  openChat: (conv: ChatConversation) => void;
  setActiveConversation: (customerId: string) => void;
  removeConversation: (customerId: string) => void;
  setIsOpen: (open: boolean) => void;
  setIsMinimized: (min: boolean) => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const registerChat = useCallback((conv: ChatConversation) => {
    setConversations(prev => {
      const exists = prev.find(c => c.customerId === conv.customerId);
      if (exists) return prev;
      return [...prev, conv];
    });
    setActiveConversationId(prev => prev ?? conv.customerId);
  }, []);

  const openChat = useCallback((conv: ChatConversation) => {
    // Always ensure conversation is in the list (re-add if previously removed)
    setConversations(prev => {
      const exists = prev.find(c => c.customerId === conv.customerId);
      if (exists) return prev;
      return [...prev, conv];
    });
    setActiveConversationId(conv.customerId);
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const removeConversation = useCallback((customerId: string) => {
    setConversations(prev => {
      const next = prev.filter(c => c.customerId !== customerId);
      if (next.length === 0) {
        setIsOpen(false);
        setIsMinimized(false);
      }
      return next;
    });
    setActiveConversationId(prev => {
      if (prev === customerId) {
        const remaining = conversations.filter(c => c.customerId !== customerId);
        return remaining.length > 0 ? remaining[0].customerId : null;
      }
      return prev;
    });
  }, [conversations]);

  const setActiveConversation = useCallback((customerId: string) => {
    setActiveConversationId(customerId);
  }, []);

  return (
    <ChatContext.Provider value={{
      conversations,
      activeConversationId,
      isOpen,
      isMinimized,
      registerChat,
      openChat,
      setActiveConversation,
      removeConversation,
      setIsOpen,
      setIsMinimized,
    }}>
      {children}
    </ChatContext.Provider>
  );
}
